import type { RapierRigidBody } from '@react-three/rapier';

import { RigidBodyType } from '@dimforge/rapier3d-compat';
import { useFrame } from '@react-three/fiber';
import { useRef, useState } from 'react';
import * as THREE from 'three';

import { DRAG_LIFT_HEIGHT, DRAG_ROLL_SPEED } from './physics-constants';

const UP = new THREE.Vector3(0, 1, 0);

const ROLL_RADIUS = 0.4;

const SPRING_STRENGTH = 50;

const DRAG_DAMPING = 0.97;

// const THROW_MULTIPLIER = 1;

const MAX_THROW_SPEED = 20;

// cap chase speed
const MAX_DRAG_SPEED = 100;

export default function useEnergyDrag() {
	const [dragging, setDragging] = useState(false);

	const dragTarget = useRef(new THREE.Vector3());

	const dragVelocity = useRef(new THREE.Vector3());

	const grabOffset = useRef(new THREE.Vector3());

	const intersection = useRef(new THREE.Vector3());

	const previousPosition = useRef(new THREE.Vector3());

	const accumulatedRotation = useRef(new THREE.Quaternion());

	const rollQuaternion = useRef(new THREE.Quaternion());

	const rollAxis = useRef(new THREE.Vector3());

	const startingY = useRef(0);

	function beginDrag(e: any, body: RapierRigidBody, mesh: THREE.Mesh) {
		e.stopPropagation();

		e.target.setPointerCapture(e.pointerId);

		setDragging(true);

		body.setBodyType(RigidBodyType.KinematicPositionBased, true);

		const translation = body.translation();

		startingY.current = translation.y;

		mesh.getWorldPosition(previousPosition.current);

		dragTarget.current.copy(previousPosition.current);

		dragVelocity.current.set(0, 0, 0);

		accumulatedRotation.current.copy(mesh.quaternion);

		if (
			e.ray.intersectPlane(
				new THREE.Plane(
					UP.clone(),
					-(startingY.current + DRAG_LIFT_HEIGHT)
				),
				intersection.current
			)
		) {
			grabOffset.current
				.copy(intersection.current)
				.sub(previousPosition.current);
		}
	}

	function updateDragTarget(e: any) {
		if (!dragging) return;

		e.stopPropagation();

		const plane = new THREE.Plane(
			UP.clone(),
			-(startingY.current + DRAG_LIFT_HEIGHT)
		);

		if (!e.ray.intersectPlane(plane, intersection.current)) {
			return;
		}

		dragTarget.current.copy(intersection.current).sub(grabOffset.current);
	}

	function updatePhysics(body: RapierRigidBody, delta: number) {
		if (!dragging) return;

		const current = body.translation();

		const currentPosition = new THREE.Vector3(
			current.x,
			current.y,
			current.z
		);

		const difference = dragTarget.current.clone().sub(currentPosition);

		const acceleration = difference.multiplyScalar(SPRING_STRENGTH);

		dragVelocity.current.add(acceleration.multiplyScalar(delta));

		if (dragVelocity.current.length() > MAX_DRAG_SPEED) {
			dragVelocity.current.normalize().multiplyScalar(MAX_DRAG_SPEED);
		}

		dragVelocity.current.multiplyScalar(DRAG_DAMPING);

		const nextPosition = currentPosition.add(
			dragVelocity.current.clone().multiplyScalar(delta)
		);

		body.setNextKinematicTranslation({
			x: nextPosition.x,
			y: startingY.current + DRAG_LIFT_HEIGHT,
			z: nextPosition.z,
		});

		/*
		 * Reduced rolling effect
		 */
		const movement = nextPosition.clone().sub(previousPosition.current);

		if (movement.lengthSq() > 0.000001) {
			rollAxis.current.crossVectors(movement, UP).normalize();

			const angle = (movement.length() / ROLL_RADIUS) * DRAG_ROLL_SPEED;

			rollQuaternion.current.setFromAxisAngle(rollAxis.current, angle);

			accumulatedRotation.current.multiply(rollQuaternion.current);

			body.setNextKinematicRotation(accumulatedRotation.current);
		}

		previousPosition.current.copy(nextPosition);
	}

	function endDrag(e: any, body: RapierRigidBody) {
		if (!dragging) return;

		e.stopPropagation();

		e.target.releasePointerCapture(e.pointerId);

		setDragging(false);

		body.setBodyType(RigidBodyType.Dynamic, true);

		body.setLinvel(
			{
				x: THREE.MathUtils.clamp(
					dragVelocity.current.x,
					-MAX_THROW_SPEED,
					MAX_THROW_SPEED
				),

				y: Math.max(dragVelocity.current.y, -9.81),

				z: THREE.MathUtils.clamp(
					dragVelocity.current.z,
					-MAX_THROW_SPEED,
					MAX_THROW_SPEED
				),
			},
			true
		);
	}

	function useDragFrame(body: RapierRigidBody | null) {
		useFrame((_, delta) => {
			if (!body) return;

			updatePhysics(body, delta);
		});
	}

	return {
		dragging,

		beginDrag,

		updateDragTarget,

		endDrag,

		useDragFrame,
	};
}
